import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type Column,
} from '@/components/ui';
import { useMe } from '@/features/auth/session-context';
import {
  useRegisterActions,
  useRegisterKinds,
  useRegisterRecords,
  type RegisterKind,
  type RegisterRecord,
} from '@/features/registers/api';
import { lastPagePath } from '@/features/shell/last-page';
import { isApiError } from '@/lib/api';
import gov from '@/pages/governance/governance.module.css';
import { RecordDrawer } from '@/pages/ops/RecordDrawer';
import { dayLabel } from '@/pages/ops/register-format';
import { useStaffNames } from '@/pages/ops/register-hooks';
import styles from './shared.module.css';

const SUPPORT_KIND = 'support_ticket';

/**
 * RL-1820: any staff member reports a problem from Help (POST
 * /registers/support_ticket) and follows their own reports. The Super admin
 * sees open ones in the bell and answers them on Go-live.
 */
export function ReportProblem() {
  const kinds = useRegisterKinds();
  const kind = kinds.data?.find((k) => k.kind === SUPPORT_KIND);
  const [reporting, setReporting] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const staff = useStaffNames();
  if (!kind) return null;
  return (
    <>
      <Card
        title="My reported problems"
        flush
        actions={
          kind.canWrite && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => setReporting(true)}
            >
              Report a problem
            </Button>
          )
        }
      >
        <MyProblems kind={kind} onOpen={setOpen} />
      </Card>
      {reporting && (
        <ReportDialog
          kind={kind}
          onClose={() => setReporting(false)}
          onReported={(id) => {
            setReporting(false);
            setOpen(id);
          }}
        />
      )}
      {open && (
        <RecordDrawer
          kind={kind}
          id={open}
          staff={staff}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function MyProblems({
  kind,
  onOpen,
}: {
  kind: RegisterKind;
  onOpen: (id: string) => void;
}) {
  const me = useMe();
  const q = useRegisterRecords(kind);
  // The API lists only the caller's own unless they see every report (the
  // Super admin); Help always shows the caller's own. The API has no "mine"
  // filter for them yet, so the Super admin's list is narrowed here.
  const rows = kind.onlyOwn
    ? q.data
    : q.data?.filter((r) => r.createdBy?.id === me.id);
  const columns: Column<RegisterRecord>[] = [
    {
      key: 'ref',
      header: 'Ref',
      render: (r) => (
        <>
          <span className="mono">{r.number ?? '-'}</span>
          <span className={styles.note}> · {dayLabel(r.createdAt)}</span>
        </>
      ),
    },
    {
      key: 'title',
      header: 'Problem',
      render: (r) => <strong>{r.title}</strong>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (r) =>
        typeof r.data.priority === 'string' ? r.data.priority : '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.statusTone} dot={!r.closedAt}>
          {r.statusLabel}
        </Badge>
      ),
    },
    {
      key: 'open',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (r) => (
        <Button size="sm" onClick={() => onOpen(r.id)}>
          Open
        </Button>
      ),
    },
  ];
  return (
    <DataTable
      caption="My reported problems"
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      loading={q.isLoading}
      error={q.error}
      onRetry={() => void q.refetch()}
      empty={
        <EmptyState
          title="You have not reported a problem"
          body="Use Report a problem when something in the admin does not work."
        />
      }
    />
  );
}

type Form = { title: string; what: string; page: string; priority: string };

function ReportDialog({
  kind,
  onClose,
  onReported,
}: {
  kind: RegisterKind;
  onClose: () => void;
  onReported: (id: string) => void;
}) {
  const { create } = useRegisterActions(kind.kind);
  const toast = useToast();
  const field = (key: string) => kind.fields.find((f) => f.key === key);
  const max = (key: string, fallback: number) =>
    field(key)?.maxLength ?? fallback;
  const [form, setForm] = useState<Form>(() => ({
    title: '',
    what: '',
    page: lastPagePath() ?? '',
    priority: '',
  }));
  const [apiField, setApiField] = useState<{
    field: string;
    message: string;
  } | null>(null);
  const set = (key: keyof Form, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (apiField?.field === key) setApiField(null);
  };
  const tooLong = (key: keyof Form, n: number) =>
    form[key].trim().length > n ? `Keep it to ${n} characters.` : undefined;
  const errors = {
    title: tooLong('title', max('title', 120)),
    what: tooLong('what', max('what', 2000)),
    page: tooLong('page', max('page', 200)),
  };
  const errorFor = (key: keyof typeof errors) =>
    errors[key] ?? (apiField?.field === key ? apiField.message : undefined);
  const ready =
    !!form.title.trim() &&
    !!form.what.trim() &&
    !Object.values(errors).some(Boolean);
  const priority = field('priority');

  const send = () =>
    create
      .mutateAsync({
        data: {
          title: form.title.trim(),
          what: form.what.trim(),
          ...(form.page.trim() ? { page: form.page.trim() } : {}),
          ...(form.priority ? { priority: form.priority } : {}),
        },
      })
      .then((r) => {
        toast.show({
          tone: 'success',
          title: r.number ? `Reported as ${r.number}` : 'Reported',
          body: 'The Super admin has been told.',
        });
        onReported(r.id);
      })
      .catch((err: unknown) => {
        const f = isApiError(err) ? err.problem?.field : undefined;
        // A field the form shows gets the message under it; any other
        // (or Priority when the kind has no options) is a toast.
        const onForm =
          typeof f === 'string' &&
          (['title', 'what', 'page'].includes(f) ||
            (f === 'priority' && !!priority?.options));
        if (isApiError(err) && onForm)
          setApiField({ field: f, message: err.message });
        else
          toast.show({
            tone: 'error',
            title: isApiError(err)
              ? (err.problem?.title ?? 'Not sent')
              : 'Not sent',
            body: isApiError(err) ? err.message : undefined,
            error: err,
          });
      });

  return (
    <Dialog
      open
      variant="modal"
      title="Report a problem"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={create.isPending}
            onClick={() => void send()}
          >
            Send report
          </Button>
        </>
      }
    >
      <div className={styles.reportForm}>
        <Field label="Title" error={errorFor('title')}>
          <Input
            placeholder="Receipt does not print"
            value={form.title}
            onChange={(ev) => set('title', ev.target.value)}
          />
        </Field>
        <Field
          label="What happened"
          hint="What you did, what you expected and what you saw."
          error={errorFor('what')}
        >
          <textarea
            className={gov.textarea}
            value={form.what}
            onChange={(ev) => set('what', ev.target.value)}
          />
        </Field>
        <Field
          label="Page"
          hint={field('page')?.hint ?? 'The page you were on.'}
          error={errorFor('page')}
        >
          <Input
            value={form.page}
            onChange={(ev) => set('page', ev.target.value)}
          />
        </Field>
        {priority?.options && (
          <Field
            label="Priority"
            hint={priority.hint}
            error={
              apiField?.field === 'priority' ? apiField.message : undefined
            }
          >
            <Select
              value={form.priority}
              placeholder="Not sure (P3)"
              options={priority.options}
              onChange={(ev) => set('priority', ev.target.value)}
            />
          </Field>
        )}
        <p className={styles.note}>
          Screenshots cannot be attached yet; describe what you saw.
        </p>
      </div>
    </Dialog>
  );
}
